import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { SUBSTANCES } from "@/data/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import {
  Cigarette, Cloud, Package, Info, CalendarClock, Trash2, Plus, Flag, TriangleAlert, Sparkles, ArrowRight, Map, RotateCcw, Award, HeartHandshake,
} from "lucide-react";

const ICONS = { Cigarette, Cloud, Package };

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

function PlanTimeline({ plan }) {
  const sub = SUBSTANCES[plan.substance];
  const steps = plan.schedule || plan.steps || [];
  const attempts = plan.attempts || 1;
  return (
    <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="plan-timeline">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-2xl text-olive capitalize">{sub?.label} plan</h3>
            {attempts > 1 && (
              <span data-testid="plan-attempt-badge" className="inline-flex items-center gap-1 rounded-full bg-brand-teal/15 text-brand-teal-dark px-2.5 py-1 text-xs font-semibold">
                <Award className="w-3.5 h-3.5" /> Attempt #{attempts}
              </span>
            )}
          </div>
          <p className="text-sm text-stone">
            {plan.method === "cold_turkey"
            ? "Cold turkey"
            : `Gentle taper · ${steps.filter((s) => !s.is_quit_day).length} steps`} · Quit day {new Date(plan.quit_date).toLocaleDateString()}
          </p>
        </div>
      </div>
      {attempts > 1 && (
        <div className="flex items-start gap-3 rounded-2xl bg-sage p-4 mb-5" data-testid="plan-perseverance">
          <HeartHandshake className="w-5 h-5 text-brand-teal-dark shrink-0 mt-0.5" />
          <p className="text-sm text-olive/80">
            You've begun again {attempts - 1} {attempts - 1 === 1 ? "time" : "times"} — that's perseverance, not failure. Every fresh start is faith in action.
          </p>
        </div>
      )}
      {plan.restarts?.length > 0 && (
        <div className="rounded-2xl border border-border p-4 mb-5" data-testid="restart-history">
          <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-3">Your attempts so far</p>
          <div className="space-y-2.5">
            {plan.restarts.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-sm" data-testid={`restart-entry-${i}`}>
                <span className="w-6 h-6 rounded-full bg-sage text-olive/70 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-olive/80">
                  Began again {new Date(r.restarted_at).toLocaleDateString()}
                  {r.days_reached > 0 ? ` · reached ${r.days_reached} smoke-free ${r.days_reached === 1 ? "day" : "days"}` : " · a fresh restart"}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-3 text-sm" data-testid="restart-current">
              <span className="w-6 h-6 rounded-full bg-brand-teal/15 text-brand-teal-dark text-xs font-semibold flex items-center justify-center shrink-0">{attempts}</span>
              <span className="text-brand-teal-dark font-medium">This attempt — in progress. Keep going.</span>
            </div>
          </div>
        </div>
      )}
      {plan.medical_warning && (
        <div className="flex gap-3 rounded-2xl bg-terracotta/10 text-terracotta p-4 mb-5 text-sm">
          <TriangleAlert className="w-5 h-5 shrink-0" />
          <p>{sub?.blurb} Please do not stop abruptly on your own.</p>
        </div>
      )}
      <div className="relative pl-6">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
        {steps.map((step, i) => {
          const past = new Date(step.date) <= new Date();
          return (
            <div key={i} className="relative pb-6 last:pb-0" data-testid={`schedule-step-${i}`}>
              <div className={`absolute -left-6 top-1 w-3.5 h-3.5 rounded-full border-2 ${step.is_quit_day ? "bg-gold border-gold" : past ? "bg-sky border-sky" : "bg-sand border-stone/40"}`} />
              <div className="flex items-baseline justify-between gap-3">
                <p className={`font-medium ${step.is_quit_day ? "text-gold" : "text-olive"} flex items-center gap-1.5`}>
                  {step.is_quit_day && <Flag className="w-4 h-4" />}{step.label}
                </p>
                <p className="text-xs text-stone shrink-0">{new Date(step.date).toLocaleDateString()}</p>
              </div>
              <p className="text-sm text-olive/70 mt-0.5">{step.note}</p>
              {!step.is_quit_day && (
                <p className="text-xs text-sky font-medium mt-1">Target: {step.target} {sub?.unit}/day</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PlanPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restartFor, setRestartFor] = useState(null);
  const [restartDate, setRestartDate] = useState("");
  const [restarting, setRestarting] = useState(false);

  const [form, setForm] = useState({
    substance: "cigarettes",
    method: "taper",
    baseline: 20,
    weeks: 6,
    cost_per_unit: 0.65,
    quit_date: addDaysISO(42),
    reason: "",
  });

  const load = () => {
    client.get("/plans").then((r) => {
      setPlans(r.data);
      setCreating(r.data.length === 0);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sub = SUBSTANCES[form.substance];

  const setSubstance = (key) => {
    const s = SUBSTANCES[key];
    setForm((f) => ({ ...f, substance: key, baseline: s.defaultBaseline, cost_per_unit: s.defaultCost }));
    setPreview(null);
  };

  const doPreview = async () => {
    try {
      const res = await client.post("/plans/preview", {
        substance: form.substance,
        method: form.method,
        baseline: Number(form.baseline),
        unit: sub.unit,
        weeks: Number(form.weeks),
        cost_per_unit: Number(form.cost_per_unit),
        quit_date: form.quit_date,
        reason: form.reason,
      });
      setPreview({ ...res.data, schedule: res.data.steps, substance: form.substance, method: form.method, weeks: form.weeks, quit_date: form.quit_date });
    } catch (e) {
      toast.error("Could not generate preview. Check your inputs.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await client.post("/plans", {
        substance: form.substance,
        method: form.method,
        baseline: Number(form.baseline),
        unit: sub.unit,
        weeks: Number(form.weeks),
        cost_per_unit: Number(form.cost_per_unit),
        quit_date: form.quit_date,
        reason: form.reason,
      });
      toast.success("Your plan is ready. One gentle step at a time.");
      setCreating(false);
      setPreview(null);
      setLoading(true);
      load();
    } catch (e) {
      toast.error("Could not save your plan.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await client.delete(`/plans/${id}`);
    toast.success("Plan removed.");
    setLoading(true);
    load();
  };

  const openRestart = (p) => {
    setRestartFor(p);
    setRestartDate(addDaysISO(Math.max(7, (p.weeks || 6) * 7)));
  };
  const doRestart = async () => {
    if (!restartFor || !restartDate) return;
    setRestarting(true);
    try {
      await client.post(`/plans/${restartFor.id}/restart`, { quit_date: restartDate });
      toast.success("A fresh start — this attempt counts. Keep going, one day at a time.");
      setRestartFor(null);
      setLoading(true);
      load();
    } catch (e) {
      toast.error("Could not restart your plan. Please try again.");
    } finally {
      setRestarting(false);
    }
  };

  if (loading) return <div className="animate-pulse text-stone">Loading your plan…</div>;

  return (
    <div className="space-y-8" data-testid="plan-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">Your journey, mapped</p>
          <h1 className="font-serif text-display text-olive">Taper &amp; quit plan</h1>
        </div>
        {!creating && (
          <Button data-testid="new-plan-btn" onClick={() => { setCreating(true); setPreview(null); }} className="rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2">
            <Plus className="w-4 h-4" /> New plan
          </Button>
        )}
      </div>

      <Link to="/app/strategy" data-testid="plan-strategy-link"
        className="group flex items-start gap-4 rounded-3xl bg-brand-navy text-white p-6 shadow-soft transition-transform duration-200 hover:scale-[1.005]">
        <div className="rounded-2xl bg-white/10 w-12 h-12 flex items-center justify-center shrink-0"><Map className="w-5 h-5 text-brand-teal" /></div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.15em] text-brand-teal mb-1">Personalized strategy</p>
          <h3 className="font-serif text-2xl">Break your triggers &amp; plan the first days</h3>
          <p className="text-white/70 text-sm mt-1.5">Turn your plan into action — trigger swaps, new habits, and a gentle 4-night getaway to get through the hardest stretch.</p>
        </div>
        <ArrowRight className="w-5 h-5 mt-1 shrink-0 text-white/60 transition-transform duration-200 group-hover:translate-x-1" />
      </Link>

      {creating && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="rounded-3xl bg-card border border-border p-7 shadow-soft space-y-6">
            <div>
              <Label className="text-olive mb-3 block">What are you quitting?</Label>
              <div className="grid grid-cols-3 gap-3">
                {Object.values(SUBSTANCES).map((s) => {
                  const Icon = ICONS[s.icon] || Cigarette;
                  const active = form.substance === s.key;
                  return (
                    <button key={s.key} data-testid={`substance-${s.key}`} onClick={() => setSubstance(s.key)}
                      className={`rounded-2xl border p-4 flex flex-col items-center gap-2 text-center transition-colors duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-border hover:bg-sage"}`}>
                      <Icon className={`w-6 h-6 ${active ? "text-terracotta" : "text-sky"}`} />
                      <span className="text-xs font-medium text-olive leading-tight">{s.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone mt-3">{sub.blurb}</p>
            </div>

            <div className="flex items-start gap-3 rounded-2xl bg-sky/10 border border-sky/20 p-4" data-testid="medical-tip">
              <Info className="w-4 h-4 text-sky shrink-0 mt-0.5" />
              <p className="text-xs text-olive/80 leading-relaxed">
                <span className="font-medium text-olive">Health-guideline note:</span> quitting all at once is the most effective approach, but cutting down to quit works too — especially with <span className="font-medium">nicotine replacement (a patch plus gum or lozenge)</span> and support. Ask your doctor or pharmacist about NRT. This app offers encouragement, not medical advice.
              </p>
            </div>

            <div>
              <Label className="text-olive mb-3 block">Approach</Label>
              <div className="grid grid-cols-2 gap-3">
                {[{ k: "taper", t: "Gentle taper", d: "Step down gradually" }, { k: "cold_turkey", t: "Cold turkey", d: "Stop on quit day" }].map((m) => (
                  <button
                    key={m.k}
                    data-testid={`method-${m.k}`}
                    onClick={() => { setForm((f) => ({ ...f, method: m.k })); setPreview(null); }}
                    className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${form.method === m.k ? "border-terracotta bg-terracotta/5" : "border-border hover:bg-sage"}`}
                  >
                    <p className="font-medium text-olive">{m.t}</p>
                    <p className="text-xs text-stone">{m.d}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="baseline" className="text-olive mb-2 block">Current use ({sub.unitShort})</Label>
                <Input id="baseline" data-testid="input-baseline" type="number" min="0" value={form.baseline}
                  onChange={(e) => { setForm((f) => ({ ...f, baseline: e.target.value })); setPreview(null); }} />
              </div>
              <div>
                <Label htmlFor="cost" className="text-olive mb-2 block">{sub.costLabel}</Label>
                <Input id="cost" data-testid="input-cost" type="number" step="0.01" min="0" value={form.cost_per_unit}
                  onChange={(e) => setForm((f) => ({ ...f, cost_per_unit: e.target.value }))} />
              </div>
            </div>

            {form.method === "taper" && (
              <div className="rounded-2xl bg-sage p-4 text-sm text-olive/80">
                Your taper will step down gently from <span className="font-medium">today</span> until your quit day
                {form.quit_date ? ` (${new Date(form.quit_date).toLocaleDateString()})` : ""}. Choose a quit day that gives
                you enough room to reduce comfortably — the further out it is, the gentler each step.
              </div>
            )}

            <div>
              <Label htmlFor="quit" className="text-olive mb-2 block">Quit day</Label>
              <Input id="quit" data-testid="input-quitdate" type="date" min={todayISO()} value={form.quit_date}
                onChange={(e) => { setForm((f) => ({ ...f, quit_date: e.target.value })); setPreview(null); }} />
              <p className="text-xs text-stone mt-2">
                {form.method === "taper"
                  ? "The timeline runs from today up to this day — this is your anchor."
                  : "You'll stop completely on this day."}
              </p>
            </div>

            <div>
              <Label htmlFor="reason" className="text-olive mb-2 block">Your reason (a why to hold onto)</Label>
              <Textarea id="reason" data-testid="input-reason" rows={2} placeholder="For my family, my health, my walk with God…"
                value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>

            <Button data-testid="preview-btn" onClick={doPreview} variant="outline" className="w-full rounded-full gap-2 border-olive/20 hover:bg-sage">
              <Sparkles className="w-4 h-4" /> Generate my schedule
            </Button>
          </div>

          {/* Preview */}
          <div>
            {preview ? (
              <div className="space-y-4">
                <PlanTimeline plan={{ ...preview, baseline: form.baseline, unit: sub.unit }} />
                <Button data-testid="save-plan-btn" disabled={saving} onClick={save} className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 py-6">
                  {saving ? "Saving…" : <>Save my plan <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-border p-10 h-full flex flex-col items-center justify-center text-center text-stone">
                <CalendarClock className="w-10 h-10 mb-4 text-sky" />
                <p className="font-serif text-2xl text-olive mb-1">Your schedule appears here</p>
                <p className="text-sm">Fill in your details and press “Generate my schedule”.</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {!creating && plans.map((p) => (
        <div key={p.id} className="relative">
          <PlanTimeline plan={p} />
          <button data-testid={`delete-plan-${p.id}`} onClick={() => remove(p.id)} className="absolute top-7 right-7 rounded-full p-2 text-stone hover:bg-terracotta/10 hover:text-terracotta transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          <button data-testid={`restart-plan-${p.id}`} onClick={() => openRestart(p)}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl border border-border py-3 text-sm font-medium text-olive/80 hover:bg-sage hover:text-brand-teal-dark transition-colors">
            <RotateCcw className="w-4 h-4" /> Fell off? Begin again — it still counts
          </button>
        </div>
      ))}

      <ResponsiveModal open={!!restartFor} onOpenChange={(o) => !o && setRestartFor(null)} testid="restart-modal"
        icon={<div className="rounded-xl bg-brand-teal/15 p-2 shrink-0"><RotateCcw className="w-4 h-4 text-brand-teal-dark" /></div>}
        title="Begin again"
        description="A setback is information, not failure. Slips are part of almost every quit — what matters is that you're starting again."
        footer={
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button data-testid="restart-cancel" variant="outline" onClick={() => setRestartFor(null)} className="flex-1 rounded-full border-olive/20 hover:bg-sage">Not now</Button>
            <Button data-testid="restart-confirm" onClick={doRestart} disabled={restarting || !restartDate} className="flex-1 rounded-full bg-terracotta hover:bg-terracotta-soft text-white gap-2">
              {restarting ? "Starting…" : <>Start fresh <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        }>
        <div className="space-y-4 pb-2">
          <div className="rounded-2xl bg-sage p-4 text-sm text-olive/80">
            This re-anchors your taper to today and resets your day count and money saved. Your history stays —
            this will be recorded as <span className="font-medium text-olive">attempt #{(restartFor?.attempts || 1) + 1}</span>.
          </div>
          <div>
            <Label htmlFor="restart-date" className="text-olive mb-2 block">Your new quit day</Label>
            <Input id="restart-date" data-testid="restart-quitdate" type="date" min={todayISO()} value={restartDate}
              onChange={(e) => setRestartDate(e.target.value)} />
            <p className="text-xs text-stone mt-2">Pick a day that gives you gentle room to step down again.</p>
          </div>
          <p className="font-scripture italic text-scripture text-olive/80 border-l-2 border-gold pl-3">
            “For though the righteous fall seven times, they rise again.” — Proverbs 24:16
          </p>
        </div>
      </ResponsiveModal>
    </div>
  );
}
